import { WriteRepository } from './WriteRepository';

const WRITE_TARGETS = Object.freeze({
  firestore: 'firestore',
  api: 'api'
});

function normalizeTarget(value, fallback = WRITE_TARGETS.firestore) {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === WRITE_TARGETS.api ? WRITE_TARGETS.api : fallback;
}

function parseOperationRouting(value) {
  const map = {};
  const text = String(value || '').trim();
  if (!text) {
    return map;
  }

  text.split(',').forEach(part => {
    const [operation, target] = part.split(':').map(v => String(v || '').trim());
    if (!operation) {
      return;
    }
    map[operation] = normalizeTarget(target);
  });

  return map;
}

export function getWriteRoutingConfigFromEnv(env = process.env) {
  return {
    defaultTarget: normalizeTarget(env.VUE_APP_WRITE_REPOSITORY || env.VUE_APP_WRITE_TARGET),
    operationTargets: parseOperationRouting(env.VUE_APP_WRITE_REPOSITORY_BY_OPERATION)
  };
}

export class CompositeWriteRepository extends WriteRepository {
  constructor({ firestoreRepo, apiRepo, routing = {} }) {
    super();
    this.firestoreRepo = firestoreRepo;
    this.apiRepo = apiRepo;
    this.routing = {
      defaultTarget: normalizeTarget(routing.defaultTarget),
      operationTargets: routing.operationTargets || {}
    };
  }

  repoFor(operation, options = {}) {
    const routeKey = options.routeKey;
    const configuredTarget = this.routing.operationTargets[routeKey]
      || this.routing.operationTargets[operation]
      || this.routing.defaultTarget;
    const target = normalizeTarget(configuredTarget);
    const repo = target === WRITE_TARGETS.api ? this.apiRepo : this.firestoreRepo;
    if (!repo || typeof repo[operation] !== 'function') {
      throw new Error(`Write repository target "${target}" does not implement ${operation}`);
    }
    return repo;
  }

  async patchDoc(path, patch, options = {}) {
    return this.repoFor('patchDoc', options).patchDoc(path, patch, options);
  }

  async setDoc(path, data, options = {}) {
    return this.repoFor('setDoc', options).setDoc(path, data, options);
  }

  async createDoc(collectionPath, data, options = {}) {
    return this.repoFor('createDoc', options).createDoc(collectionPath, data, options);
  }

  async deleteDoc(path, options = {}) {
    return this.repoFor('deleteDoc', options).deleteDoc(path, options);
  }
}
